import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { originOf, buildPointBuffers, verticalFrame } from '../lib/dataset-points3d';

function CloudPoints({ ext, origin, refElev, groundY }) {
  const geometry = useMemo(() => {
    const b = buildPointBuffers(ext, origin, verticalFrame({ refElev, groundY }));
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(b.positions, 3));
    g.setAttribute('color', new THREE.BufferAttribute(b.colors, 3));
    return g;
  }, [ext, origin, refElev, groundY]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  return (
    <points geometry={geometry} frustumCulled={false}>
      <pointsMaterial vertexColors size={2.5} sizeAttenuation={false} />
    </points>
  );
}

/**
 * One THREE.Points per checked dataset with x/y/z. `clouds` (id -> extract) comes from
 * hooks/useDatasetPoints. Points are placed in the shared local frame `origin`; without one the first
 * drawn dataset's centroid becomes the frame. Rendered inside the terrain group (which carries the
 * vertical exaggeration) with `refElev` / `groundY`, elevations line up with the DEM; without them y = z.
 */
export default function DatasetPointClouds({ datasets, clouds, origin, refElev = null, groundY = null }) {
  const ready = datasets
    .map(d => ({ id: d.id, ext: clouds?.[d.id] }))
    .filter(c => c.ext && c.ext.count > 0);
  const firstExt = ready[0]?.ext;
  const frame = useMemo(() => origin || originOf(firstExt), [origin, firstExt]);
  if (!frame) return null;
  return <>{ready.map(c => <CloudPoints key={c.id} ext={c.ext} origin={frame} refElev={refElev} groundY={groundY} />)}</>;
}
